import { useMemo, useState, useCallback } from "react";
import { SearchBoxChangeEvent } from "@fluentui/react-components";
import { InputOnChangeData } from "@fluentui/react-components";

interface IUseAdminListSearchConfig<T> {
  items: T[];
  searchFields: (keyof T)[];
  minLength?: number;
  controlledQuery?: string;
  onControlledQueryChange?: (value: string) => void;
}

interface IUseAdminListSearchReturn<T> {
  searchQuery: string;
  handleSearchChange: (
    event: SearchBoxChangeEvent | React.ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData | { value: string },
  ) => void;
  handleSearchValueChange: (value: string) => void;
  filteredItems: T[];
}

export function useAdminListSearch<T>(
  config: IUseAdminListSearchConfig<T>,
): IUseAdminListSearchReturn<T> {
  const [internalQuery, setInternalQuery] = useState<string>("");
  const minLength = config.minLength ?? 1;

  const isControlled = config.controlledQuery !== undefined;
  const searchQuery = isControlled ? config.controlledQuery! : internalQuery;

  const handleSearchChange = useCallback(
    (
      event: SearchBoxChangeEvent | React.ChangeEvent<HTMLInputElement>,
      data?: InputOnChangeData | { value: string },
    ): void => {
      const value = data?.value ?? "";
      if (isControlled) {
        config.onControlledQueryChange?.(value);
      } else {
        setInternalQuery(value);
      }
    },
    [isControlled, config.onControlledQueryChange],
  );

  const handleSearchValueChange = useCallback(
    (value: string): void => {
      if (isControlled) {
        config.onControlledQueryChange?.(value);
      } else {
        setInternalQuery(value);
      }
    },
    [isControlled, config.onControlledQueryChange],
  );

  const filteredItems = useMemo(() => {
    if (!searchQuery || searchQuery.length < minLength) {
      return config.items;
    }

    const lowerQuery = searchQuery.toLowerCase();

    return config.items.filter((item: T) => {
      return config.searchFields.some((field: keyof T) => {
        const fieldValue = item[field];

        if (fieldValue === null || fieldValue === undefined) {
          return false;
        }

        return String(fieldValue).toLowerCase().includes(lowerQuery);
      });
    });
  }, [config.items, searchQuery, config.searchFields, minLength]);

  return {
    searchQuery,
    handleSearchChange,
    handleSearchValueChange,
    filteredItems,
  };
}
